import { ErrorPage } from '../core/errorPage';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/**
 * Parses TechDocs URL path to extract entity information and document path
 * Implements input validation for security compliance (OWASP Top 10)
 *
 * @param path - The URL path to parse (e.g., "/docs/default/component/my-service/overview/")
 * @returns Parsed entity information with sanitized docPath or null if invalid
 */
const parseTechDocsPath = (
  path: string,
): {
  namespace: string;
  kind: string;
  name: string;
  docPath: string;
} | null => {
  // Input validation for security (prevent path traversal attacks)
  if (!path || typeof path !== 'string' || path.length > 2000) {
    return null;
  }

  // TechDocs URLs follow pattern: /docs/:namespace/:kind/:name/*
  const match = path.match(/^\/docs\/([^\/]+)\/([^\/]+)\/([^\/]+)(?:\/(.*))?$/);

  if (match) {
    const [, namespace, kind, name, rawDocPath = ''] = match;

    // Remove trailing slash and sanitize docPath for security
    const docPath = rawDocPath
      ? `${rawDocPath
          .replace(/\/+$/, '') // Remove trailing slashes
          .replace(/\.\.+/g, '') // Prevent directory traversal
          .trim()}.md`
      : 'index.md';

    // Validate extracted components
    if (![namespace, kind, name].every(part => !!part)) {
      return null;
    }

    return { namespace, kind, name, docPath };
  }

  return null;
};

const getAnnotationMetadata = (prefix: string, value?: string) => {
  if (value && value.startsWith(prefix)) {
    return value.substring(prefix.length);
  }
  return value;
};

export const TechDocsNotFound = () => {
  const { pathname } = useLocation();
  const catalogApi = useApi(catalogApiRef);

  const {
    value: repoUrl,
    error: entityError,
    loading: entityLoading,
  } = useAsync(async () => {
    if (!pathname) {
      throw new Error('Invalid TechDocs path');
    }

    const parsedPath = parseTechDocsPath(pathname);

    const { namespace, kind, name, docPath } = parsedPath!;
    const entityRef = `${kind}:${namespace}/${name}`;

    const fetchedEntity = await catalogApi.getEntityByRef(entityRef);

    if (!fetchedEntity) {
      throw new Error('Entity not found in catalog');
    }

    // Maybe use startswith with a combination of substring instead of using replace
    const sourceLocation = getAnnotationMetadata(
      'url:',
      fetchedEntity.metadata.annotations?.['backstage.io/source-location'],
    );

    const techDocsRef = getAnnotationMetadata(
      'dir:',
      fetchedEntity.metadata.annotations?.['backstage.io/techdocs-ref'],
    );

    return `${sourceLocation}/${techDocsRef}/docs/${docPath}`;
  }, [pathname]);

  if (entityLoading) {
    return <div>Loading...</div>;
  }

  if (repoUrl) {
    return (
      <ErrorPage
        status="404"
        statusMessage="Documentation Not Found"
        additionalInfo="The documentation you're looking for might be available directly on GitHub:"
        suggestedLink={repoUrl}
      />
    );
  }
  return <ErrorPage status="404" statusMessage="Documentation Not Found" additionalInfo={entityError?.message} />;
};
